import React, { Ref, useEffect, useRef, useState } from 'react';
import { MapRef, MapProps } from 'react-map-gl';
import { getStandardMapStyles } from '../components/outage/outage-center-map/MapStyleStandard.descriptor';
import { getHybridMapStyles } from '../components/outage/outage-center-map/MapStyleHybrid.descriptor';
import config from '../config';
import mapboxgl from 'mapbox-gl';
import { useIsMobile } from '../util/style-utils';

const apiKey = config.amazonLocationService.key;
const region = config.amazonLocationService.region;

export const getDefaultMapStyle = () => getStandardMapStyles(apiKey, region);

//[west, south, east, north]
export const maxBoundsCoordinates = [
  -124.73192788144308,
  44.5964720761396,
  -120.67058366715625,
  46.136350221553904,
] as [number, number, number, number];

//[west, south, east, north]
const maxBoundsCoordinatesMobile = [
  -123.98043532453156,
  43.5670478734651,
  -121.4338148112241,
  47.070729601612754,
] as [number, number, number, number];

export const DEFAULT_MAP_LAT = 45.2707;
export const DEFAULT_MAP_LNG = -122.7746;
export const MIN_START_ZOOM = 12;
export const MIN_MAP_ZOOM = 8;
export const MIN_MAP_ZOOM_MOBILE = 6;
export const MAX_MAP_ZOOM = 15;
export const MAX_CLUSTER_ZOOM = 14;

export enum MapStyle {
  Standard = 'Standard',
  Hybrid = 'Hybrid',
  Satellite = 'Satellite',
}

export interface OutageLngLat {
  lat: number;
  lng: number;
}

interface UpdateMapViewportProps {
  zoom: number;
  latitude: number;
  longitude: number;
  duration?: number;
}

export interface ALSMapProperties {
  mapContainerRef: React.MutableRefObject<HTMLDivElement | null>;
  mapInstance: mapboxgl.Map | null;
  mapStyle: MapStyle;
  mapOptions: MapProps & { ref: Ref<MapRef> };
  changeMapStyle: (style: MapStyle) => void;
  updateViewPort: (newViewport: UpdateMapViewportProps) => void;
  fitBounds: (latLngs: OutageLngLat[]) => void;
}

const useOutagesMap = (mapOptionsOverride: MapProps = {}): ALSMapProperties => {
  const [mapStyle, setMapStyle] = useState<MapStyle>(MapStyle.Standard);
  const isMobile = useIsMobile();

  const mapRef = useRef<MapRef>(null);
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstance = mapRef?.current ? mapRef?.current?.getMap() : null;

  useEffect(() => {
    if (mapInstance) {
      mapInstance.setMinZoom(isMobile ? MIN_MAP_ZOOM_MOBILE : MIN_MAP_ZOOM);
      mapInstance.setMaxBounds(
        isMobile ? maxBoundsCoordinatesMobile : maxBoundsCoordinates,
      );
      mapInstance.touchZoomRotate.disableRotation();
    }
  }, [isMobile, mapInstance]);

  const updateViewPort = ({
    latitude,
    longitude,
    zoom,
    duration = 0,
  }: UpdateMapViewportProps) => {
    mapInstance?.easeTo({
      center: [longitude, latitude],
      zoom: zoom,
      duration,
    });
  };

  const fitBounds = (points: OutageLngLat[]) => {
    if (!points || points.length === 0 || !mapInstance) {
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();

    for (const p of points) {
      bounds.extend([p.lng, p.lat]);
    }

    mapInstance?.fitBounds(bounds, { padding: 100, duration: 800 });
  };

  const changeMapStyle = (style: MapStyle) => {
    setMapStyle(style);
  };

  return {
    mapContainerRef,
    mapStyle,
    mapOptions: {
      mapStyle:
        mapStyle === MapStyle.Hybrid
          ? (getHybridMapStyles(
              apiKey,
              region,
            ) as import('@maplibre/maplibre-gl-style-spec').StyleSpecification)
          : (getStandardMapStyles(
              apiKey,
              region,
            ) as import('@maplibre/maplibre-gl-style-spec').StyleSpecification),
      maxZoom: MAX_MAP_ZOOM,
      scrollZoom: true,
      dragRotate: false,
      attributionControl: false,
      ...mapOptionsOverride,
      ref: mapRef,
    },
    mapInstance,
    changeMapStyle,
    updateViewPort,
    fitBounds,
  };
};

export default useOutagesMap;
